import { useEffect, useState } from 'react'
import { TVHomeScreen } from './TVHomeScreen'
import { TVRoomScreen } from './TVRoomScreen'
import { ensureAnonSession } from '../lib/api/tv'
import { readError } from '../lib/errors'
import { useT } from '../i18n'

/**
 * TV entrypoint. Parses the URL to decide whether to show the landing
 * screen (/tv) or a specific room (/tv/CODE).
 *
 * The TV signs in anonymously so it can create rooms and subscribe
 * to Realtime — it is not a player.
 */
export function TVApp() {
  const t = useT()
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Read path segment after /tv. Empty = landing, non-empty = room code.
  const roomCode = parseRoomCode(window.location.pathname)

  useEffect(() => {
    let cancelled = false
    // TV is a pure display — no seat, no profile name. Passing a name
    // here would clobber the user's real profile.display_name if this
    // tab shares a browser with the main app.
    ensureAnonSession()
      .then(() => { if (!cancelled) setReady(true) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  if (error) {
    return <TVMessage title={t("Couldn't start the TV session")} body={error} />
  }
  if (!ready) {
    return <TVMessage title={t('Starting up…')} body={t('Connecting to the party server.')} />
  }
  if (roomCode) {
    return <TVRoomScreen code={roomCode} />
  }
  return <TVHomeScreen />
}

function parseRoomCode(path: string): string | null {
  // /tv, /tv/, /tv/ABCDEF → 'ABCDEF' | null
  const m = path.match(/^\/tv\/?([A-Za-z0-9]+)?\/?$/)
  const code = m?.[1]
  if (!code) return null
  return code.toUpperCase()
}

/** Stateless TV full-screen message. Used for loading / errors. */
function TVMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-text p-12">
      <div className="text-center max-w-3xl">
        <h1 className="font-display text-5xl font-bold mb-4">{title}</h1>
        <p className="text-xl text-brand-muted">{body}</p>
      </div>
    </div>
  )
}
